import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_ENABLED, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '@/config/supabase';
import { cosmeticById } from '@/config/cosmetics';
import type { SaveData, VehicleLoadout } from '@/core/SaveData';
import { toGuestSave } from '@/core/SaveData';
import {
  adoptGarage,
  applyClaim,
  claimAmount,
  clearGarageDirty,
  garageAction,
  importAmount,
  isUserSwitch,
  mergeCityRecords,
  reconcileWallet,
  type ServerGarage,
  type ServerWallet,
} from '@/account/accountLogic';
import {
  IAP_BACKGROUND_WAITS_MS,
  IAP_CLAIM_BUDGET_MS,
  IAP_CLAIM_TIMEOUT_MS,
  IAP_CLAIM_TRIES,
  IAP_FOREGROUND_WAITS_MS,
  SP_ON_THE_WAY,
  claimVerdict,
  type ClaimVerdict,
} from '@/account/iapCredit';
import { clearPendingReferral, pendingReferral } from '@/account/referral';
import {
  parseAdminReports,
  type AdminReport,
  type ModerationAction,
} from '@/social/moderation';
import { NATIVE_AUTH_CALLBACK, isNativeApp, nativeReady, nativeShims } from '@/native/shims';

/**
 * AccountService.ts — the RACER ID shell (M100): sign-in (Google / Apple /
 * email magic link + 6-digit code), the persistent session, and the sync
 * engine that keeps the local save reconciled against the server wallet.
 *
 * DESIGN RULES (hard-won, keep):
 *  - supabase-js is DYNAMICALLY imported (its own chunk) — the game's first
 *    paint never waits on the account layer. If the page landed on an auth
 *    redirect (?code=...) the import starts immediately so the exchange runs;
 *    otherwise it starts one tick after boot.
 *  - Sessions persist in localStorage with auto-refresh (supabase defaults) —
 *    the owner's "stay signed in for a seamless experience" requirement.
 *    PKCE flow. NOTE the PKCE trap: a magic LINK only completes in the
 *    browser profile that requested it (the code_verifier lives there). The
 *    email face therefore always offers the 6-digit CODE too — verifyOtp
 *    works from anywhere, and it's the future Capacitor path.
 *  - The sync engine NEVER holds a long-lived save clone: every step re-reads
 *    through the bridge, transforms, and writes back — an award banked
 *    mid-sync can never be clobbered. Every server write is idempotent, so
 *    any step may safely repeat after a crash or a dropped connection.
 *  - All user-id trust lives SERVER-side (auth.uid() in the RPCs); nothing
 *    here sends a user id.
 */

export type AccountStatus = 'disabled' | 'loading' | 'signedOut' | 'signedIn';

/** Gold / silver / bronze counts ([1st, 2nd, 3rd]) for races and tours. */
export interface TrophyCounts {
  race: [number, number, number];
  tour: [number, number, number];
}

export interface AccountState {
  status: AccountStatus;
  userId: string | null;
  email: string | null;
  /** 'google' | 'apple' | 'email' — for the "signed in via" line. */
  provider: string | null;
  /** World rank (all-time), refreshed on each sync. */
  rank: number | null;
  /** The profile's SERVER-side handle (the generated RACER-XXXX until the
   *  player names themselves). Display fallback only — never enters the save. */
  nickname: string | null;
  syncing: boolean;
  /** True once a sync has completed for the current session. */
  synced: boolean;
  /** M101: has this ACCOUNT completed (or been grandfathered past) the
   *  post-signup onboarding? null until the first wallet pull answers. */
  onboarded: boolean | null;
  /** M101: this profile's unique share code (dribbleracer.com/?ref=CODE). */
  referralCode: string | null;
  /** M101: friends who signed up via the link / SP those referrals earned. */
  refClaimed: number;
  refPoints: number;
  /** M101b: this account sees the Admin Dashboard (owner accounts only —
   *  granted manually by SQL; the server re-checks on every stats read). */
  isAdmin: boolean;
  /** M156: this Racer ID is SUSPENDED by a moderator (a report was upheld).
   *  The server already refuses its Racer Club calls and renames; the app
   *  uses this to keep it out of online play and explain why. */
  banned: boolean;
  /**
   * M121: does this account own the base game?
   *
   * THREE-STATE ON PURPOSE. `null` means "not answered yet" — the wallet pull
   * has not landed, or we are offline. `null` MUST NEVER GATE: an owner whose
   * wallet is still in flight would otherwise be shown a paywall for a game
   * they already paid for. That is the same lesson `status: 'loading'` encodes
   * for the M111 boot gate, applied one level deeper.
   *
   * Computed SERVER-side inside my_wallet as "an unrevoked base_game
   * entitlement exists OR this profile predates the grandfather cutoff" — the
   * cutoff lives in the migration, never in the bundle, because a client-side
   * cutoff is a text file anyone can edit.
   *
   * This lives on AccountState and NOT in SaveData, deliberately: SaveData
   * survives a user switch and would leak one account's entitlement to the
   * next signer-in (the M103 identity bug, and the `unlockedVehicles` bug
   * class). The offline-grace cache is a separate, explicitly-cleared thing.
   */
  ownsGame: boolean | null;
  /** M121: when the entitlement was granted — the Founder cosmetic's cutoff
   *  input. Captured now because it is impossible to reconstruct later. */
  baseGameSince: string | null;
}

/** M101b: the Admin Dashboard's growth/usage stats (admin_stats RPC). */
export interface AdminStats {
  totalPlayers: number;
  new24h: number;
  new7d: number;
  onboardedPlayers: number;
  referralSignups: number;
  spTotal: number;
  sp7d: number;
  active7d: number;
  purchasesTotal: number;
}

/** How Flow exposes the save + UI hooks to the service. */
export interface AccountBridge {
  getSave(): SaveData;
  /** Persist + refresh whatever account-aware UI is visible. */
  setSave(save: SaveData): void;
  onState(state: AccountState): void;
  /** The guest pot landed in the wallet ("+N SKILL POINTS" toast). */
  onClaim(points: number): void;
}

interface LeaderboardRowWire {
  user_id: string;
  nickname: string;
  points: number;
  rank: number;
}

const SYNC_DEBOUNCE_MS = 1500;

export class AccountService {
  private client: SupabaseClient | null = null;
  private clientPromise: Promise<SupabaseClient | null> | null = null;
  private state: AccountState = {
    status: SUPABASE_ENABLED ? 'loading' : 'disabled',
    userId: null,
    email: null,
    provider: null,
    rank: null,
    nickname: null,
    syncing: false,
    synced: false,
    onboarded: null,
    referralCode: null,
    refClaimed: 0,
    refPoints: 0,
    isAdmin: false,
    banned: false,
    ownsGame: null,
    baseGameSince: null,
  };
  private syncTimer: number | null = null;
  private syncing = false;
  private syncAgain = false;
  /** M110: the in-flight runSync, so signOut can genuinely WAIT for it. The
   *  old `await this.runSync()` was a no-op while a sync was running (the
   *  re-entrancy guard returns immediately), and the still-running sync could
   *  then write `account` back into the save AFTER sign-out unlinked it. */
  private syncRun: Promise<void> | null = null;

  /**
   * M121 — the Stripe success landing (`?session_id=cs_...`), captured at
   * construction BEFORE anything rewrites the URL, then confirmed on the next
   * sync and cleared.
   *
   * This is the "player got back before the webhook did" path. It NEVER grants
   * anything on its own authority — it calls `confirm-checkout`, which
   * re-retrieves the session from Stripe and runs the SAME idempotent fulfil
   * routine the webhook runs. Stripe is explicit that webhooks are required
   * and that you cannot fulfil from the landing page alone; this only removes
   * the wait when both arrive.
   */
  private pendingCheckoutSession: string | null = null;

  /** M123 — latched at construction: this page load arrived on the Stripe
   *  SUCCESS url (a well-formed `?session_id=` was captured). Read-only —
   *  Flow's boot routing uses it to front the purchase screen on its
   *  CONFIRMING face instead of the title. The confirm logic itself is
   *  untouched: the session id is still consumed by the M121 sync step, and
   *  the webhook remains the authoritative grant path. */
  private readonly wasCheckoutReturn: boolean;

  /** M123 — latched at construction: this page load arrived on the Stripe
   *  CANCEL url (`?checkout=cancelled`, stripped here so a reload or a shared
   *  link can never replay it). Nothing was charged; Flow lands the player
   *  back on the paywall's calm pitch face. */
  private readonly wasCheckoutCancel: boolean;

  /** M130 — the SP-pack id when this page load is a return from an SP-pack
   *  checkout (`&sp=<pack>` on the success or cancel URL), else null. Lets
   *  Flow land the player back in the SHOP with a toast instead of on the
   *  base-game paywall faces. Stripped from the URL like the session id. */
  private readonly spPackReturn: string | null;

  constructor(private bridge: AccountBridge) {
    this.pendingCheckoutSession = this.takeCheckoutSessionFromUrl();
    this.wasCheckoutReturn = this.pendingCheckoutSession !== null;
    this.wasCheckoutCancel = this.takeCheckoutCancelledFromUrl();
    this.spPackReturn = this.takeSpPackFromUrl();
  }

  /** M130 — see {@link spPackReturn}. */
  get checkoutSpPack(): string | null {
    return this.spPackReturn;
  }

  /** M130 — read and strip `?sp=<pack id>` (the SP-pack checkout's success
   *  AND cancel URLs carry it), the same one-shot idiom as the session id. */
  private takeSpPackFromUrl(): string | null {
    try {
      const url = new URL(window.location.href);
      const raw = url.searchParams.get('sp');
      if (raw === null) return null;
      url.searchParams.delete('sp');
      window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
      return /^sp\.[a-z0-9_]{1,24}$/.test(raw) ? raw : null;
    } catch {
      return null;
    }
  }

  /** M123 — see {@link wasCheckoutReturn}. */
  get returnedFromCheckout(): boolean {
    return this.wasCheckoutReturn;
  }

  /** M123 — see {@link wasCheckoutCancel}. */
  get checkoutCancelled(): boolean {
    return this.wasCheckoutCancel;
  }

  /** Read and strip `?session_id=`, so a reload or a shared link can never
   *  replay it. Shape-checked: Stripe session ids are `cs_`-prefixed. */
  private takeCheckoutSessionFromUrl(): string | null {
    try {
      const url = new URL(window.location.href);
      const raw = url.searchParams.get('session_id');
      if (!raw) return null;
      url.searchParams.delete('session_id');
      window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
      return /^cs_[A-Za-z0-9_]{4,120}$/.test(raw) ? raw : null;
    } catch {
      return null;
    }
  }

  /** M123 — read and strip `?checkout=cancelled` (the create-checkout edge
   *  function's cancel_url), same one-shot idiom as the session id above. */
  private takeCheckoutCancelledFromUrl(): boolean {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('checkout') !== 'cancelled') return false;
      url.searchParams.delete('checkout');
      window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
      return true;
    } catch {
      return false;
    }
  }

  get enabled(): boolean {
    return SUPABASE_ENABLED;
  }

  getState(): AccountState {
    return { ...this.state };
  }

  /** Boot the account layer. Never throws; never blocks the caller. */
  init(): void {
    if (!SUPABASE_ENABLED) {
      this.setState({ status: 'disabled' });
      return;
    }
    // M111: the app shell's OAuth/magic-link redirects arrive as a
    // dribbleracer:// deep link (never as page params) — native init routes
    // them here for the PKCE code exchange.
    nativeShims.onAuthCallback = (url) => void this.completeNativeAuth(url);
    // An OAuth/magic-link landing (?code= / ?error_description=) must be
    // consumed promptly; a plain boot defers the chunk off the critical path.
    const params = new URLSearchParams(window.location.search);
    const authLanding = params.has('code') || params.has('error_description');
    if (authLanding) void this.ensureClient();
    else window.setTimeout(() => void this.ensureClient(), 400);
  }

  /** Create (once) the supabase client + wire auth state → sync. */
  private async ensureClient(): Promise<SupabaseClient | null> {
    if (this.client) return this.client;
    if (this.clientPromise) return this.clientPromise;
    this.clientPromise = (async () => {
      try {
        // M111: the native shim registry must be filled BEFORE the client is
        // built — its storage adapter (Preferences → UserDefaults) is what
        // keeps a boot-gated app signed in through OS storage pressure. On
        // the web this await is already-resolved and the options are today's.
        await nativeReady();
        const { createClient } = await import('@supabase/supabase-js');
        const client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
          auth: {
            flowType: 'pkce',
            persistSession: true,
            autoRefreshToken: true,
            // Natively there is no URL to detect a session in (deep links
            // route through completeNativeAuth instead).
            detectSessionInUrl: !nativeShims.platform,
            storage: nativeShims.authStorage,
          },
        });
        this.client = client;
        client.auth.onAuthStateChange((event, session) => {
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            if (session) {
              this.reflectSession(session);
              this.scheduleSync(0);
            } else if (this.state.status === 'loading') {
              this.setState({ status: 'signedOut' });
            }
          } else if (event === 'SIGNED_OUT') {
            this.setState({
              status: 'signedOut',
              userId: null,
              email: null,
              provider: null,
              rank: null,
              nickname: null,
              synced: false,
              onboarded: null,
              referralCode: null,
              refClaimed: 0,
              refPoints: 0,
              isAdmin: false,
              banned: false,
              // M121: an explicit sign-out CLEARS ownership. It must never
              // linger as "this device remembers someone owned it" — that
              // would hand a free game to the next person who signs in here
              // (the M103 never-leak rule, applied to the entitlement).
              ownsGame: null,
              baseGameSince: null,
            });
          }
        });
        this.cleanAuthParamsFromUrl();
        return client;
      } catch {
        // Chunk failed (offline first load, blocked CDN…): guest mode until
        // the next page load. The game is fully playable without it.
        this.setState({ status: 'signedOut' });
        this.clientPromise = null;
        return null;
      }
    })();
    return this.clientPromise;
  }

  /** Strip one-time auth params so reloads/shares never replay them. Game
   *  params (?net=&room=…) are preserved — only the auth ones go. */
  private cleanAuthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    const authParams = ['code', 'state', 'error', 'error_code', 'error_description'];
    if (!authParams.some((p) => url.searchParams.has(p))) return;
    for (const p of authParams) url.searchParams.delete(p);
    window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
  }

  private reflectSession(session: Session): void {
    this.setState({
      status: 'signedIn',
      userId: session.user.id,
      email: session.user.email ?? null,
      provider: (session.user.app_metadata?.provider as string | undefined) ?? 'email',
    });
  }

  private setState(patch: Partial<AccountState>): void {
    this.state = { ...this.state, ...patch };
    this.bridge.onState(this.getState());
  }

  // ---- Sign-in flows (each resolves to null on success / an error line) ----

  async signInWithProvider(provider: 'google' | 'apple'): Promise<string | null> {
    const client = await this.ensureClient();
    if (!client) return 'Connection unavailable — try again.';

    // M111 — iOS: Sign in with Apple is the NATIVE sheet (App-Review's
    // expectation for an account-gated app). The raw nonce goes to Supabase,
    // its SHA-256 went to Apple; signInWithIdToken verifies the pair.
    if (provider === 'apple' && nativeShims.appleSignIn) {
      try {
        const cred = await nativeShims.appleSignIn();
        if (!cred) return null; // user closed the sheet — not an error
        const { error } = await client.auth.signInWithIdToken({
          provider: 'apple',
          token: cred.identityToken,
          nonce: cred.nonce,
        });
        return error ? friendlyAuthError(error.message) : null;
      } catch {
        return 'Sign-in hiccup — try again in a moment.';
      }
    }

    // M111 — app shell (Google on both platforms, Apple on Android): the
    // OAuth page opens in the SYSTEM browser (embedded WebViews are blocked
    // by Google) and comes back via the dribbleracer:// deep link, where
    // completeNativeAuth exchanges the PKCE code. Success is therefore
    // reported by the SIGNED_IN event, not by this promise.
    if (nativeShims.openAuthSession) {
      const { data, error } = await client.auth.signInWithOAuth({
        provider,
        options: { redirectTo: NATIVE_AUTH_CALLBACK, skipBrowserRedirect: true },
      });
      if (error || !data?.url) {
        return friendlyAuthError(error?.message ?? 'no auth url');
      }
      try {
        await nativeShims.openAuthSession(data.url);
        return null;
      } catch {
        return 'Couldn’t open the sign-in page — try again.';
      }
    }

    const { error } = await client.auth.signInWithOAuth({
      provider,
      // MUST be the bare origin root (vite base './' — a sub-path callback
      // breaks asset resolution; pinned in vite.config.ts).
      options: { redirectTo: `${location.origin}${location.pathname}` },
    });
    return error ? friendlyAuthError(error.message) : null; // success → page navigates away
  }

  /** M111: finish a native OAuth/magic-link round trip. The deep link carries
   *  the one-time ?code=; exchanging it against this client's stored PKCE
   *  verifier signs us in (the SIGNED_IN event then drives sync + UI). */
  async completeNativeAuth(url: string): Promise<void> {
    const client = await this.ensureClient();
    if (!client) return;
    try {
      const code = new URL(url).searchParams.get('code');
      if (!code) return; // an error/cancel landing — the screen just stays put
      await client.auth.exchangeCodeForSession(code);
    } catch {
      /* expired/foreign code — the player simply retries from the screen */
    }
  }

  async sendMagicLink(email: string): Promise<string | null> {
    const client = await this.ensureClient();
    if (!client) return 'Connection unavailable — try again.';
    const { error } = await client.auth.signInWithOtp({
      email,
      options: {
        // M111: in the app shell the emailed link finishes on the deep link
        // (tap it on the phone → the app opens and exchanges the code); the
        // 6-digit code stays the works-from-anywhere path either way.
        emailRedirectTo: nativeShims.platform
          ? NATIVE_AUTH_CALLBACK
          : `${location.origin}${location.pathname}`,
        shouldCreateUser: true,
      },
    });
    return error ? friendlyAuthError(error.message) : null;
  }

  async verifyEmailCode(email: string, code: string): Promise<string | null> {
    const client = await this.ensureClient();
    if (!client) return 'Connection unavailable — try again.';
    const { error } = await client.auth.verifyOtp({ email, token: code.trim(), type: 'email' });
    return error ? friendlyAuthError(error.message) : null; // success → SIGNED_IN event syncs
  }

  /** Sign out THIS device: flush first, then unlink the save (guest mode
   *  again). The account and its points live server-side.
   *
   *  M110 — TWO fixes over the M100 behaviour:
   *  (1) The outboxes are NO LONGER cleared. An offline sign-out used to
   *      discard unflushed awards/purchases forever (the flush failed silently
   *      and the wipe ran anyway). They stay queued now: the same user's next
   *      sign-in flushes them (idempotent by event/item id), and a DIFFERENT
   *      user's sign-in drops them at the user-switch step — they can never
   *      post to the wrong account.
   *  (2) An IN-FLIGHT sync is awaited for real before unlinking. The old
   *      `await runSync()` returned immediately when a sync was already
   *      running, and that sync's reconcile then re-wrote `account` into the
   *      save after sign-out had nulled it — a signed-out device that still
   *      believed it was linked. */
  async signOut(): Promise<void> {
    if (this.syncTimer !== null) window.clearTimeout(this.syncTimer);
    this.syncAgain = false; // a queued follow-up must not race the unlink
    try {
      await this.syncRun; // let any in-flight pass finish writing first
      await this.runSync(); // then one best-effort flush of the outboxes
      await this.syncRun; // runSync() may have been the re-entrancy no-op
    } catch {
      /* offline sign-out is fine — idempotent events, nothing double-posts */
    }
    this.syncAgain = false;
    // M138: the device becomes a GUEST — the account's career, garage and
    // records leave with the session (toGuestSave; the server restores them
    // on the next sign-in, the outboxes are kept). M121's offline-grace
    // ownership cache dies with it too: it covers "could not reach the
    // server", not "someone once owned this on this device".
    const save = toGuestSave(this.bridge.getSave());
    this.bridge.setSave(save);
    // M140: the social layer tidies up WHILE the session is still valid —
    // this device's push token is unregistered (a shared phone must never
    // ping the previous Racer ID about crew races) and the live inbox
    // channel closes. Bounded: a dead network never holds the sign-out.
    if (this.beforeSignOut) {
      try {
        await Promise.race([
          this.beforeSignOut(),
          new Promise<void>((r) => window.setTimeout(r, 2500)),
        ]);
      } catch {
        /* best effort — the token is also re-homed on the next registration */
      }
    }
    try {
      await this.client?.auth.signOut({ scope: 'local' });
    } catch {
      /* session storage cleared locally regardless */
    }
    this.setState({
      status: 'signedOut',
      userId: null,
      email: null,
      provider: null,
      rank: null,
      nickname: null,
      synced: false,
      onboarded: null,
      referralCode: null,
      refClaimed: 0,
      refPoints: 0,
      isAdmin: false,
      banned: false,
    });
  }

  /**
   * M121 — start the $9.99 web purchase. ONE call to the `create-checkout`
   * edge function, which reads the buyer's identity from the JWT (never from
   * anything this client sends), creates a Stripe-hosted Checkout Session and
   * hands back its URL. We then hand off to Stripe.
   *
   * THE BROWSER NEVER TOUCHES A CARD AND SHIPS NO STRIPE CODE — the whole
   * payment surface is Stripe's hosted page, so the bundle gains zero bytes
   * and there is no PCI surface here at all.
   *
   * The returned URL is NOT trusted blindly: it must be an https URL on a
   * Stripe host. A compromised or mis-deployed function must not be able to
   * bounce a player to an arbitrary origin.
   *
   * Resolves null when it navigated away, or a player-readable error line.
   * NOTE the redirect NEVER grants anything — the webhook does, and
   * `confirm-checkout` covers the case where the player gets back first.
   */
  async startCheckout(pack?: string): Promise<string | null> {
    // M124: the app shell is a PAID store listing — there is nothing to sell
    // inside it, and sending an app user to Stripe would break Apple 3.1.1.
    // No caller reaches here on native (every gate says no), but the one
    // function that can navigate to a payment page must refuse on its own.
    if (isNativeApp()) return 'Already included with the app.';
    const client = await this.ensureClient();
    if (!client || this.state.status !== 'signedIn') {
      return 'Sign in to your Racer ID first.';
    }
    try {
      // M130: with a pack id this starts an SP-PACK checkout instead of the
      // base game; the edge function validates the id against sp_packs and
      // owns the price — the client never sends an amount.
      const res = await client.functions.invoke(
        'create-checkout',
        pack ? { body: { pack } } : undefined,
      );
      if (res.error) return 'Couldn’t start checkout — nothing was charged.';
      const url = (res.data as { url?: unknown } | null)?.url;
      if (typeof url !== 'string' || !this.isStripeCheckoutUrl(url)) {
        return 'Couldn’t start checkout — nothing was charged.';
      }
      window.location.assign(url);
      return null;
    } catch {
      return 'Couldn’t reach checkout — nothing was charged.';
    }
  }

  /** M133 — the store's localised pack prices (native app only). */
  async spStorePrices(packIds: readonly string[]): Promise<Record<string, string>> {
    const uid = this.state.userId;
    if (!isNativeApp() || !nativeShims.iap || this.state.status !== 'signedIn' || !uid) return {};
    return nativeShims.iap.prices(packIds, uid);
  }

  /** M133 — buy an SP pack INSIDE the store app. The store's own sheet takes
   *  the payment; the credit arrives by RevenueCat's webhook, which its docs
   *  put at "usually 5 to 60 seconds".
   *
   *  M135 — on iOS the app first asks our server to confirm the purchase
   *  with Apple (`claim-iap`), which credits it in a second or two. The
   *  webhook stays the safety net, so the wait below is unchanged.
   *
   *  M134 — the wait is two-phase (account/iapCredit.ts): a short one the
   *  sheet holds for ('done' = the points are in the wallet), then a long
   *  quiet one ('pending' = still on the way — the toast fires when they
   *  land; `paid` says whether the store CONFIRMED the payment or the app
   *  only could not rule it out). `onPaid` fires the moment the store
   *  confirms the payment.
   *  Resolves an error line ONLY when nothing was charged. */
  async buySpNative(
    packId: string,
    onPaid?: () => void,
  ): Promise<
    | string
    | { status: 'done' | 'cancelled' }
    | { status: 'pending'; note: string; paid: boolean }
  > {
    const iap = nativeShims.iap;
    if (!isNativeApp() || !iap) return 'Purchases are not available here.';
    const uid = this.state.userId;
    if (this.state.status !== 'signedIn' || !uid) return 'Sign in to your Racer ID first.';
    const before = this.bridge.getSave().purchasedPoints;
    const r = await iap.buy(packId, uid);
    if (r === 'cancelled') return { status: 'cancelled' };
    if ('error' in r) return r.error;
    if ('unsure' in r) {
      // The payment may have been taken (the confirmation failed on the way
      // back, or it waits on an approval): keep looking for the credit.
      void this.awaitSpCredit(uid, before, [
        ...IAP_FOREGROUND_WAITS_MS,
        ...IAP_BACKGROUND_WAITS_MS,
      ]);
      return { status: 'pending', note: r.unsure, paid: false };
    }
    onPaid?.();
    // M135: with Apple's transaction number in hand, ask our server to
    // confirm it with Apple NOW (the pass with no wait), and again on the
    // next passes if Apple was not ready. Without one, or when the check
    // cannot help, this is the M134 wait for the webhook, unchanged.
    const claim = r.paid
      ? { txnId: r.paid, left: IAP_CLAIM_TRIES, budgetMs: IAP_CLAIM_BUDGET_MS }
      : undefined;
    const waits = claim ? [0, ...IAP_FOREGROUND_WAITS_MS] : IAP_FOREGROUND_WAITS_MS;
    if (await this.awaitSpCredit(uid, before, waits, claim)) return { status: 'done' };
    void this.awaitSpCredit(uid, before, IAP_BACKGROUND_WAITS_MS);
    return { status: 'pending', note: SP_ON_THE_WAY, paid: true };
  }

  /** M135 — ask `claim-iap` to confirm one purchase with Apple and credit it.
   *  Never throws and never blocks the wait for long: no answer in time is
   *  just "ask again". */
  private async claimIap(txnId: string, timeoutMs: number): Promise<ClaimVerdict> {
    try {
      const client = await this.ensureClient();
      if (!client) return 'stop';
      const res = await Promise.race([
        client.functions.invoke('claim-iap', { body: { txn: txnId } }),
        new Promise<null>((done) => setTimeout(() => done(null), timeoutMs)),
      ]);
      if (!res || res.error) return 'retry';
      return claimVerdict(res.data);
    } catch {
      return 'retry';
    }
  }

  /** Pull the wallet on a schedule until the bought points show. Stops the
   *  moment they do, or when a different Racer ID is on the device. */
  private async awaitSpCredit(
    uid: string,
    before: number,
    waits: readonly number[],
    claim?: { txnId: string; left: number; budgetMs: number },
  ): Promise<boolean> {
    for (const waitMs of waits) {
      if (waitMs > 0) await new Promise((res) => setTimeout(res, waitMs));
      if (this.state.userId !== uid) return false;
      if (this.bridge.getSave().purchasedPoints > before) return true;
      if (claim && claim.left > 0 && claim.budgetMs >= 500) {
        const asked = Date.now();
        const verdict = await this.claimIap(
          claim.txnId,
          Math.min(IAP_CLAIM_TIMEOUT_MS, claim.budgetMs),
        );
        claim.budgetMs -= Date.now() - asked;
        claim.left = verdict === 'retry' ? claim.left - 1 : 0;
        if (this.state.userId !== uid) return false;
        // Nothing can have changed in a pass that did not wait.
        if (verdict !== 'credited' && waitMs === 0) continue;
      }
      try {
        await this.runSync();
        await this.syncRun;
      } catch {
        /* offline blip — the next pass retries */
      }
      if (this.bridge.getSave().purchasedPoints > before) return true;
    }
    return false;
  }

  /** Only ever hand off to Stripe's own hosted checkout. */
  private isStripeCheckoutUrl(raw: string): boolean {
    try {
      const u = new URL(raw);
      if (u.protocol !== 'https:') return false;
      return u.hostname === 'checkout.stripe.com' || u.hostname.endsWith('.stripe.com');
    } catch {
      return false;
    }
  }

  /** M111 — DELETE the Racer ID (store law: Apple 5.1.1(v) + the Play
   *  account-deletion policy both require an in-app path). The server side is
   *  ONE call: the `delete-account` edge function verifies the caller's JWT
   *  and admin-deletes that auth user — the whole schema cascades from
   *  auth.users (profiles → point_events/purchases/city_records/referrals/
   *  platform_links, verified live), so nothing is left behind. Locally we
   *  then unlink like a sign-out, but ALSO drop the outboxes (their account
   *  no longer exists) — the device keeps its local unlocks as a guest.
   *  Resolves null on success / a player-readable error line. */
  async deleteAccount(onStep?: (text: string) => void): Promise<string | null> {
    const client = await this.ensureClient();
    if (!client || this.state.status !== 'signedIn') {
      return 'Connection unavailable — try again.';
    }
    // M156: an Apple Racer ID in the iPhone app confirms with Apple once
    // more, so the server can REVOKE the Apple sign-in before deleting
    // (Apple's account-deletion rule for Sign in with Apple). Closing the
    // Apple sheet cancels the deletion; Apple being unavailable does not
    // block it (deletion must always work).
    let body: { apple_code: string } | undefined;
    if (nativeShims.appleReauthCode && (await this.hasAppleIdentity(client))) {
      onStep?.('CONFIRM WITH APPLE TO FINISH…');
      const r = await nativeShims.appleReauthCode();
      if (r === 'cancelled') return 'Deletion cancelled — nothing was deleted.';
      if (r) body = { apple_code: r.code };
      onStep?.('DELETING…');
    }
    try {
      const res = await client.functions.invoke('delete-account', body ? { body } : undefined);
      if (res.error) return 'Couldn’t delete the account — try again.';
    } catch {
      return 'Couldn’t delete the account — try again.';
    }
    if (this.syncTimer !== null) window.clearTimeout(this.syncTimer);
    this.syncAgain = false;
    // M138: the device becomes a guest (the deleted account's career and
    // garage must not linger on it).
    const save = toGuestSave(this.bridge.getSave());
    // The account is GONE — a later sign-in (any user) is a fresh link, and
    // the dead account's queued awards/purchases must never post anywhere.
    save.lastAccountId = null;
    save.pendingAwards = [];
    save.pendingPurchases = [];
    // M121: the account is gone, so the entitlement is gone with it. The
    // payment RECORD survives server-side for accounting and dispute
    // evidence, but this device must not keep behaving like an owner.
    save.ownsGameCached = false;
    this.bridge.setSave(save);
    try {
      await this.client?.auth.signOut({ scope: 'local' });
    } catch {
      /* the server-side user is already deleted; local storage clears */
    }
    this.setState({
      status: 'signedOut',
      userId: null,
      email: null,
      provider: null,
      rank: null,
      nickname: null,
      synced: false,
      onboarded: null,
      referralCode: null,
      refClaimed: 0,
      refPoints: 0,
      isAdmin: false,
      banned: false,
    });
    return null;
  }

  /** M101: claim a globally-unique username, with explicit feedback.
   *  reason: '' (ok) | 'taken' | 'invalid' | 'offline'. A successful claim
   *  also completes the onboarding essential step server-side. */
  async claimUsername(name: string): Promise<{ ok: boolean; reason: string; nickname: string }> {
    const client = await this.ensureClient();
    if (!client || this.state.status !== 'signedIn') {
      return { ok: false, reason: 'offline', nickname: '' };
    }
    try {
      const res = await client.rpc('claim_username', { p_name: name });
      const row = (res.data as { ok: boolean; reason: string; nickname: string }[] | null)?.[0];
      if (res.error || !row) return { ok: false, reason: 'offline', nickname: '' };
      if (row.ok) this.setState({ nickname: row.nickname, onboarded: true });
      return row;
    } catch {
      return { ok: false, reason: 'offline', nickname: '' };
    }
  }

  /**
   * Racer ID dashboard — the TROPHY CABINET: how many gold / silver / bronze
   * finishes this account has, for single races and for whole tours. Counted
   * from the account's own ledger (point_events — row-level security already
   * limits a player to their own rows), six count-only HEAD requests, so no
   * rows travel. Tours count both online and offline finales (M136: every
   * offline podium tour now pays — and so posts — its own event; before M136
   * only first-time upgrades were recorded, so older repeats stay uncounted).
   * Only Racer ID play is on the ledger — guest history never counts. null =
   * couldn't ask (signed out / offline).
   */
  async fetchTrophies(): Promise<TrophyCounts | null> {
    const client = this.client;
    const userId = this.state.userId;
    if (!client || this.state.status !== 'signedIn' || !userId) return null;
    const count = async (kinds: string[], place: number): Promise<number> => {
      const { count: n, error } = await client
        .from('point_events')
        .select('event_id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .in('kind', kinds)
        .eq('place', place);
      if (error) throw error;
      return n ?? 0;
    };
    const tours = ['tour_finale_online', 'tour_finale_offline'];
    try {
      const [r1, r2, r3, t1, t2, t3] = await Promise.all([
        count(['race'], 1),
        count(['race'], 2),
        count(['race'], 3),
        count(tours, 1),
        count(tours, 2),
        count(tours, 3),
      ]);
      return { race: [r1, r2, r3], tour: [t1, t2, t3] };
    } catch {
      return null;
    }
  }

  /** M140: the signed-in client for the social layer (RACE CREW + FRIENDS —
   *  src/social/SocialService.ts). null while signed out or when the account
   *  chunk could not load. The server reads the player from the session JWT,
   *  exactly like every other RPC here. */
  async socialClient(): Promise<SupabaseClient | null> {
    if (this.state.status !== 'signedIn') return null;
    return this.ensureClient();
  }

  /** M140: run (bounded) right before a sign-out drops the session. */
  beforeSignOut: (() => Promise<void>) | null = null;

  /** M101: batch availability check (≤8 names/call server-side). Your OWN
   *  current name reads as available to you. null = couldn't ask (offline). */
  async checkUsernames(names: string[]): Promise<Record<string, boolean> | null> {
    const client = this.client;
    if (!client || this.state.status !== 'signedIn' || names.length === 0) return null;
    try {
      const res = await client.rpc('username_available', { p_names: names.slice(0, 8) });
      if (res.error) return null;
      const out: Record<string, boolean> = {};
      for (const row of (res.data ?? []) as { name: string; available: boolean }[]) {
        out[row.name] = row.available === true;
      }
      return out;
    } catch {
      return null;
    }
  }

  /** M101b: the Admin Dashboard's stats pull. null = not admin / offline
   *  (the server re-checks the flag — the client bit is display-only). */
  async fetchAdminStats(): Promise<AdminStats | null> {
    const client = this.client;
    if (!client || this.state.status !== 'signedIn') return null;
    try {
      const res = await client.rpc('admin_stats');
      const w = (
        res.data as
          | {
              total_players: number;
              new_24h: number;
              new_7d: number;
              onboarded_players: number;
              referral_signups: number;
              sp_total: number;
              sp_7d: number;
              active_7d: number;
              purchases_total: number;
            }[]
          | null
      )?.[0];
      if (res.error || !w) return null;
      return {
        totalPlayers: Number(w.total_players) || 0,
        new24h: Number(w.new_24h) || 0,
        new7d: Number(w.new_7d) || 0,
        onboardedPlayers: Number(w.onboarded_players) || 0,
        referralSignups: Number(w.referral_signups) || 0,
        spTotal: Number(w.sp_total) || 0,
        sp7d: Number(w.sp_7d) || 0,
        active7d: Number(w.active_7d) || 0,
        purchasesTotal: Number(w.purchases_total) || 0,
      };
    } catch {
      return null;
    }
  }

  /** M156: does this Racer ID sign in with Apple? (Its identities list.) */
  private async hasAppleIdentity(client: SupabaseClient): Promise<boolean> {
    try {
      const { data } = await client.auth.getUser();
      return (data.user?.identities ?? []).some((i) => i.provider === 'apple');
    } catch {
      return false;
    }
  }

  /** M156: ADMIN DASHBOARD → REPORTS. null = not admin / offline (the server
   *  re-checks is_admin on every call; the client bit is display-only). */
  async fetchAdminReports(status: 'open' | 'all'): Promise<AdminReport[] | null> {
    const client = this.client;
    if (!client || this.state.status !== 'signedIn') return null;
    try {
      const res = await client.rpc('admin_reports', { p_status: status, p_limit: 80 });
      if (res.error) return null;
      return parseAdminReports(res.data);
    } catch {
      return null;
    }
  }

  /** M156: one moderator action on a report (rename / suspend / dismiss…). */
  async adminModerate(
    reportId: string,
    action: ModerationAction,
  ): Promise<{ status: string; newName?: string; closed?: number }> {
    const client = this.client;
    if (!client || this.state.status !== 'signedIn') return { status: 'offline' };
    try {
      const res = await client.rpc('admin_moderate', { p_report: reportId, p_action: action });
      if (res.error) return { status: 'offline' };
      const r = (res.data ?? {}) as { status?: string; new_name?: string; closed?: number };
      return {
        status: typeof r.status === 'string' ? r.status : 'offline',
        newName: typeof r.new_name === 'string' ? r.new_name : undefined,
        closed: typeof r.closed === 'number' ? r.closed : undefined,
      };
    } catch {
      return { status: 'offline' };
    }
  }

  /** M101: silently complete onboarding (the grandfather path — a brand-new
   *  account on a device that already has progress skips the flow). Optimistic
   *  locally; the RPC is idempotent and the next wallet pull confirms. */
  markOnboarded(): void {
    if (this.state.onboarded !== false) return;
    this.setState({ onboarded: true });
    if (!this.client || this.state.status !== 'signedIn') return;
    void this.client.rpc('mark_onboarded').then(() => undefined);
  }

  /** Ask for a sync soon (debounced — award bursts collapse into one run). */
  scheduleSync(delayMs: number = SYNC_DEBOUNCE_MS): void {
    if (!SUPABASE_ENABLED) return;
    if (this.syncTimer !== null) window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(() => {
      this.syncTimer = null;
      void this.runSync();
    }, delayMs);
  }

  /** The world board for the leaderboard screen (null → caller falls back to
   *  the local ladder). Rows carry SERVER ranks (ties share a rank). */
  async fetchLeaderboard(kind: 'alltime' | 'week'): Promise<{
    rows: { id: string; name: string; points: number; rank: number; isPlayer: boolean }[];
    playerRank: number;
    playerPoints: number;
  } | null> {
    const client = this.client;
    if (!client || this.state.status !== 'signedIn') return null;
    try {
      const [board, mine] = await Promise.all([
        client.rpc('leaderboard', { p_kind: kind, p_limit: 20 }),
        client.rpc('my_rank', { p_kind: kind }),
      ]);
      if (board.error || mine.error) return null;
      const uid = this.state.userId;
      const rows = ((board.data ?? []) as LeaderboardRowWire[]).map((r) => ({
        id: r.user_id,
        name: r.nickname,
        points: r.points,
        rank: Number(r.rank),
        isPlayer: r.user_id === uid,
      }));
      const my = (mine.data as { rank: number; points: number }[] | null)?.[0];
      return {
        rows,
        playerRank: Number(my?.rank ?? 0) || rows.length + 1,
        playerPoints: my?.points ?? 0,
      };
    } catch {
      return null;
    }
  }

  // ---- The sync engine ------------------------------------------------------

  /** Full sync pass: link → import → claim → flush outboxes → reconcile.
   *  Every server call is idempotent; any step may repeat after a failure. */
  async runSync(): Promise<void> {
    if (!SUPABASE_ENABLED || this.syncing) {
      this.syncAgain = this.syncing;
      return;
    }
    // M110: the latch is set SYNCHRONOUSLY here — doSync used to set it only
    // after two awaits (ensureClient + getSession), so two runSync calls
    // landing in that window both passed the guard and ran concurrently,
    // and signOut's awaited `syncRun` tracked only the newest one.
    this.syncing = true;
    const run = this.doSync();
    this.syncRun = run;
    try {
      await run;
    } finally {
      // doSync's own finally clears the flag on its full path; this covers
      // its early returns (no client / no session), which never reach it.
      this.syncing = false;
      if (this.syncRun === run) this.syncRun = null;
    }
  }

  private async doSync(): Promise<void> {
    const client = await this.ensureClient();
    if (!client) return;
    const { data } = await client.auth.getSession();
    const session = data.session;
    if (!session) {
      if (this.state.status !== 'signedOut') {
        this.setState({ status: 'signedOut', synced: false });
      }
      return;
    }
    // (this.syncing was latched synchronously in runSync — M110.)
    this.setState({ syncing: true });
    try {
      const uid = session.user.id;
      this.reflectSession(session);

      // 0) M121 — a Stripe success landing waiting to be confirmed. Runs FIRST
      // so the wallet pull at step 7 already sees the entitlement and the
      // player is never shown a paywall a beat after paying. Cleared whatever
      // the outcome: the webhook is the authoritative path and will land
      // regardless, so a failed confirm must not retry forever.
      const checkoutSession = this.pendingCheckoutSession;
      if (checkoutSession) {
        this.pendingCheckoutSession = null;
        try {
          await client.functions.invoke('confirm-checkout', {
            body: { session_id: checkoutSession },
          });
        } catch {
          /* the webhook grants it; the next sync will see it */
        }
      }

      // 1) Link / user-switch. A different Racer ID on this device — whether
      // still linked or detected across a sign-out gap via lastAccountId
      // (M103) — adopts the NEW account's server state wholesale; the old
      // account's queued events are dropped (they can't post as someone else).
      let save = this.bridge.getSave();
      const switching = isUserSwitch(save, uid);
      if (switching || !save.account) {
        const next = structuredClone(save);
        if (switching) {
          next.pendingAwards = [];
          next.pendingPurchases = [];
          // Never import one device-career into a second account.
          next.accountImported = true;
        }
        next.account = { userId: uid };
        next.lastAccountId = uid;
        this.bridge.setSave(next);
      }

      // 2) One-time import of this device's pre-account career (server-side
      // guarded: only into a FRESH wallet, once ever, capped).
      save = this.bridge.getSave();
      if (!save.accountImported) {
        const amt = importAmount(save);
        let importedPoints = 0;
        if (amt > 0) {
          const res = await client.rpc('award_points', {
            p_event_id: 'import',
            p_kind: 'import',
            p_points: amt,
          });
          if (res.error) throw res.error;
          importedPoints = Number((res.data as { awarded: number }[] | null)?.[0]?.awarded ?? 0);
        }
        // The import took → carry the owned cosmetics over too (price-at-
        // purchase from the catalog; server validates the price set + funds).
        if (importedPoints > 0) {
          for (const itemId of save.cosmetics.owned) {
            const price = cosmeticById(itemId)?.price;
            if (!price) continue;
            const res = await client.rpc('record_purchase', {
              p_item_id: itemId,
              p_price: price,
            });
            if (res.error) throw res.error;
          }
        }
        const next = structuredClone(this.bridge.getSave());
        next.accountImported = true; // sticky either way — never retried
        this.bridge.setSave(next);
      }

      // 3) Claim the guest pot (server caps per ET day; the remainder stays
      // in the pot and claims tomorrow).
      save = this.bridge.getSave();
      const claim = claimAmount(save);
      if (claim > 0) {
        const res = await client.rpc('award_points', {
          p_event_id: `gc:${crypto.randomUUID()}`,
          p_kind: 'guest_claim',
          p_points: claim,
        });
        if (res.error) throw res.error;
        const awarded = Number((res.data as { awarded: number }[] | null)?.[0]?.awarded ?? 0);
        if (awarded > 0) {
          this.bridge.setSave(applyClaim(this.bridge.getSave(), awarded));
          this.bridge.onClaim(awarded);
        }
      }

      // 4) Flush the award outbox (idempotent by event id — replays are 0s).
      for (const entry of [...this.bridge.getSave().pendingAwards]) {
        const res = await client.rpc('award_points', {
          p_event_id: entry.id,
          p_kind: entry.kind,
          p_place: entry.place,
        });
        if (res.error) throw res.error; // rate-limit/network → retry next sync
        const next = structuredClone(this.bridge.getSave());
        next.pendingAwards = next.pendingAwards.filter((a) => a.id !== entry.id);
        this.bridge.setSave(next);
      }

      // 5) Flush the purchase outbox (idempotent by item id).
      for (const itemId of [...this.bridge.getSave().pendingPurchases]) {
        const price = cosmeticById(itemId)?.price;
        if (price) {
          const res = await client.rpc('record_purchase', { p_item_id: itemId, p_price: price });
          if (res.error) throw res.error;
        }
        const next = structuredClone(this.bridge.getSave());
        next.pendingPurchases = next.pendingPurchases.filter((id) => id !== itemId);
        this.bridge.setSave(next);
      }

      // 6) M101: a pending share-link referral claims once, right after
      // signup. The SHARER earns (20 SP, server-fixed); the server rejects
      // self/stale/replayed/over-cap claims. ANY status row is definitive —
      // clear the pending code; only a transport error keeps it for retry.
      // Never throws: a referral hiccup must not wedge the wallet sync below.
      const ref = pendingReferral();
      if (ref) {
        const refRes = await client.rpc('claim_referral', { p_code: ref });
        if (!refRes.error) clearPendingReferral();
      }

      // 7) Pull the wallet + owned set + rank, reconcile the local cache.
      const [walletRes, ownedRes, rankRes] = await Promise.all([
        client.rpc('my_wallet'),
        client.from('purchases').select('item_id'),
        client.rpc('my_rank', { p_kind: 'alltime' }),
      ]);
      if (walletRes.error) throw walletRes.error;
      const w = (
        walletRes.data as
          | {
              total: number;
              spent: number;
              weekly: number;
              finale_paid_offline: number;
              nickname: string;
              // M101 additions (older wallets can't occur — the RPC ships them).
              onboarded?: boolean;
              referral_code?: string | null;
              ref_claimed?: number;
              ref_points?: number;
              is_admin?: boolean;
              // M109 garage (older wallets can't occur — the RPC ships them).
              loadouts?: Record<string, VehicleLoadout> | null;
              player_vehicle?: string | null;
              garage_set?: boolean;
              // M121 entitlement (older wallets can't occur — the RPC ships them,
              // but they stay optional so a mid-deploy skew reads as "unknown"
              // rather than "does not own", which would paywall a paying player).
              owns_game?: boolean;
              base_game_since?: string | null;
              // M130 bought SP (optional: a pre-m130 wallet must not zero it).
              purchased?: number;
              // M156 moderation (optional: a pre-m156 wallet reads as not suspended).
              banned?: boolean;
            }[]
          | null
      )?.[0];
      if (w) {
        const wallet: ServerWallet = {
          total: w.total,
          spent: w.spent,
          weekly: w.weekly,
          finalePaidOffline: w.finale_paid_offline,
          nickname: w.nickname ?? '',
          ...(typeof w.purchased === 'number' ? { purchased: w.purchased } : {}),
        };
        const owned = ((ownedRes.data ?? []) as { item_id: string }[]).map((r) => r.item_id);
        const reconciled = reconcileWallet(this.bridge.getSave(), uid, wallet, owned, switching);
        // M121 offline grace: mirror the server's ownership answer into the
        // save so a later offline boot does not paywall a paying player. A
        // USER SWITCH writes the NEW account's answer (never the old one's) —
        // `switching` already forced the wholesale adopt above, and this rides
        // the same write, so the previous account's entitlement can never
        // survive into someone else's session on this device.
        if (typeof w.owns_game === 'boolean') reconciled.ownsGameCached = w.owns_game;
        this.bridge.setSave(reconciled);
        // M109: THE GARAGE. Runs after the purchase outbox (step 5) so the
        // server already owns everything a pushed loadout can reference —
        // otherwise set_garage's ownership check would strip a just-bought
        // item back off the car.
        await this.syncGarage(
          client,
          {
            loadouts: (w.loadouts ?? {}) as Record<string, VehicleLoadout>,
            playerVehicle: typeof w.player_vehicle === 'string' ? w.player_vehicle : null,
            everSet: w.garage_set === true,
          },
          switching,
        );
        // M110: city records (bests/unlocks) follow the account. The merge is
        // monotonic on both sides, so pushing every sync is idempotent.
        await this.syncCityRecords(client, switching);
        this.setState({
          nickname: wallet.nickname || null,
          onboarded: w.onboarded === true,
          referralCode: typeof w.referral_code === 'string' ? w.referral_code : null,
          refClaimed: Number(w.ref_claimed ?? 0) || 0,
          refPoints: Number(w.ref_points ?? 0) || 0,
          isAdmin: w.is_admin === true,
          // M156: a SUSPENDED Racer ID (moderation). Account state only — it
          // never enters the save, so it can never follow a device to the next
          // person who signs in (the M103 never-leak rule).
          banned: w.banned === true,
          // M121: three-state. A wallet that does not carry the field at all
          // (deploy skew between the migration and this client) stays `null`
          // = "unknown", which never gates. Only an explicit `false` from a
          // wallet that DOES carry the field means "does not own".
          ownsGame: typeof w.owns_game === 'boolean' ? w.owns_game : null,
          baseGameSince: typeof w.base_game_since === 'string' ? w.base_game_since : null,
        });
        // M103: the server profile is the IDENTITY AUTHORITY for a signed-in
        // device — mirror its unique username into the local save so every
        // surface (including an offline boot) shows the account's real name.
        // The old inverse rule ("push the local name up if it differs")
        // predates M101 unique usernames and could fire a rename with a
        // STALE local name — a previous account's, or a guest's — onto
        // whoever just signed in (the cross-account "Riverman" bug; only the
        // taken-name rejection contained it). Renames now travel ONLY
        // through the explicit claim paths. Gated on onboarded: a brand-new
        // account keeps the device's guest name locally so onboarding can
        // seed the name field with it.
        if (w.onboarded === true && wallet.nickname !== '') {
          const cur = this.bridge.getSave();
          if (cur.nickname !== wallet.nickname) {
            const mirrored = structuredClone(cur);
            mirrored.nickname = wallet.nickname.slice(0, 12);
            this.bridge.setSave(mirrored);
          }
        }
      }
      const rank = Number((rankRes.data as { rank: number }[] | null)?.[0]?.rank ?? 0);
      this.setState({ rank: rank > 0 ? rank : null, synced: true });
    } catch (err) {
      // Offline / rate-limited / transient — everything queued stays queued;
      // the next award or app start retries. Never surface a modal for this.
      console.warn('[racer-id] sync deferred:', err);
    } finally {
      this.syncing = false;
      this.setState({ syncing: false });
      if (this.syncAgain) {
        this.syncAgain = false;
        this.scheduleSync(250);
      }
    }
  }

  /**
   * M109 — reconcile the ACCOUNT GARAGE (what each car wears + the selected
   * ride). Owned items were always account-wide; what was EQUIPPED was not,
   * which is why one Racer ID showed different paint jobs on different devices.
   *
   * `garageAction` holds the rule (push vs adopt). After a push we adopt what
   * the server actually STORED — set_garage drops anything the account doesn't
   * own, so this keeps the device from believing in a car it isn't wearing.
   *
   * Never throws, by design: the wallet sync above it has already banked the
   * player's points, and a garage hiccup must not roll that back or wedge the
   * engine. A failed push leaves `garageDirty` set and retries next sync.
   */
  private async syncGarage(
    client: SupabaseClient,
    server: ServerGarage,
    switching: boolean,
  ): Promise<void> {
    try {
      const action = garageAction(this.bridge.getSave(), server, switching);
      if (action === 'adopt') {
        this.bridge.setSave(adoptGarage(this.bridge.getSave(), server));
        return;
      }
      if (action !== 'push') return;

      const pushed = this.bridge.getSave();
      const sent = {
        loadouts: JSON.stringify(pushed.cosmetics.loadouts),
        vehicle: pushed.settings.playerVehicle,
      };
      const res = await client.rpc('set_garage', {
        p_loadouts: pushed.cosmetics.loadouts,
        p_vehicle: pushed.settings.playerVehicle,
      });
      if (res.error) return; // stays dirty — the next sync retries
      const row = (res.data as { ok: boolean }[] | null)?.[0];
      if (!row?.ok) return;

      // The player may have equipped something else while the RPC was in
      // flight; that newer garage must stay dirty so the next sync pushes it.
      const now = this.bridge.getSave();
      if (
        JSON.stringify(now.cosmetics.loadouts) !== sent.loadouts ||
        now.settings.playerVehicle !== sent.vehicle
      ) {
        return;
      }
      // Level with the account — but KEEP the local garage rather than adopting
      // the server's echo (see clearGarageDirty for why that matters).
      this.bridge.setSave(clearGarageDirty(now));
    } catch (err) {
      console.warn('[racer-id] garage sync deferred:', err);
    }
  }

  /**
   * M110 — reconcile CITY RECORDS (per-city bests + Quick Race unlocks).
   * Push the device's records, adopt the server's merged answer, and fold it
   * with whatever was raced while the RPC was in flight — every side of that
   * is the same monotonic merge, so order can never lose a best time.
   *
   * A USER SWITCH pushes nothing (this device's records belong to whoever
   * played here before — the M103 never-leak rule) and adopts the new
   * account's records wholesale.
   *
   * Never throws: a records hiccup must not wedge the sync engine.
   */
  private async syncCityRecords(client: SupabaseClient, switching: boolean): Promise<void> {
    try {
      const pushed = switching ? {} : this.bridge.getSave().cities;
      const res = await client.rpc('merge_city_records', { p_records: pushed });
      if (res.error) return; // transient — every later sync retries
      const cur = this.bridge.getSave();
      const next = structuredClone(cur);
      next.cities = switching
        ? mergeCityRecords({}, res.data)
        : mergeCityRecords(cur.cities, res.data);
      this.bridge.setSave(next);
    } catch (err) {
      console.warn('[racer-id] city-records sync deferred:', err);
    }
  }
}

/** Supabase error strings → one player-readable line (never raw jargon). */
function friendlyAuthError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('rate limit') || m.includes('security purposes')) {
    return 'Too many attempts — wait a minute and try again.';
  }
  if (m.includes('invalid') && m.includes('email')) return 'That email doesn’t look right.';
  if (m.includes('expired') || m.includes('invalid')) {
    return 'That code didn’t match or expired — request a fresh one.';
  }
  return 'Sign-in hiccup — try again in a moment.';
}
